import React, { useEffect, useState } from 'react';
import { Input, Select, Button, DatePicker, Tag, Space, Switch, Card, Typography, Tabs, theme } from 'antd';
import { FileSearchOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, DownloadOutlined, FunctionOutlined } from '@ant-design/icons';
import axios from 'axios';
import { convertKeyToCamelCase, formatUploadDisplayName } from '../../../utils/functions';
import { getUploadBatchFileIcon } from '../../../screens/patients/uploadBatchFileIcon';
import { fieldEntityActionConfig } from './fieldEntityActionConfig';
import SingleEntitySelect from '../SingleEntitySelect.component';
import QuickPatientSelect from '../../patient/QuickPatientSelect.component';
import RandomBarcode from '../../barcode/RandomBarcode.component';
import EntityNumberInput from '../EntityNumberInput.component';
import EntityCodeInput from '../EntityCodeInput.component';
import FileUploaderAndViewer from '../../uploader/FileUploader.component';
import DatetimeInput from '../../datetime/DateTimeInput.component';

import { getTranslatedOptions } from './fieldOptions';
import i18next from 'i18next';
import { generateSessionName } from './cohortNameUtils';
import { buildTemplatePreviewHtml } from '../../../utils/emailTemplatePreview';

const { Text } = Typography;

export const TemplateContentPreview = ({ content }) => {
    const { token } = theme.useToken();
    return (
        <iframe
            title="Template HTML preview"
            sandbox=""
            srcDoc={buildTemplatePreviewHtml(content, token.colorPrimary)}
            style={{
                display: 'block',
                width: '100%',
                height: 520,
                border: `1px solid ${token.colorBorder}`,
                borderRadius: token.borderRadiusLG,
                background: '#e8edee',
            }}
        />
    );
};

const TemplateContentEditor = ({ initialValue, onCommit, value: formValue, onChange }) => {
    const [draft, setDraft] = useState(initialValue || formValue || '');

    useEffect(() => {
        setDraft(initialValue || '');
    }, [initialValue]);

    const handleChange = (event) => {
        const nextValue = event.target.value;
        setDraft(nextValue);
        onChange?.(nextValue);
        onCommit(nextValue);
    };

    return (
        <Tabs
            defaultActiveKey="html"
            style={{ width: '100%' }}
            items={[
                {
                    key: 'html',
                    label: 'HTML',
                    children: (
                        <Input.TextArea
                            value={draft}
                            onChange={handleChange}
                            placeholder="Enter template HTML. Use {placeholder} tokens such as {userName} or {email}."
                            spellCheck={false}
                            style={{
                                minHeight: 420,
                                width: '100%',
                                resize: 'vertical',
                                fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                                fontSize: 13,
                                lineHeight: 1.6,
                            }}
                        />
                    ),
                },
                {
                    key: 'preview',
                    label: 'Preview',
                    children: <TemplateContentPreview content={draft} />,
                },
            ]}
        />
    );
};

// Elegant single-file card for path fields (e.g. UploadBatch.filePath / matFilePath).
// Mirrors FileList.component.jsx's download flow (blob via sftp-download) without the full browser UI.
const iconForPath = (filePath) => {
    const lower = (filePath || '').toLowerCase();
    if (lower.endsWith('.mat')) return <FunctionOutlined style={{ fontSize: 18, opacity: 0.8 }} />;
    return getUploadBatchFileIcon(filePath, { fontSize: 18, opacity: 0.8, marginRight: 0 });
};

export const FilePathCard = ({ filePath, emptyText, userInfo, patientIdentifier }) => {
    if (!filePath) {
        return <Tag color="default">{emptyText}</Tag>;
    }
    const fileName = filePath.split('/').pop();
    const displayName = formatUploadDisplayName(fileName, patientIdentifier);

    const handleDownload = async () => {
        try {
            const response = await axios.get(
                `/dotnet_api/v1/storage/sftp-download?filePath=${encodeURIComponent(filePath)}`,
                { headers: { Authorization: `Bearer ${userInfo?.token}` }, responseType: 'blob' },
            );
            const link = document.createElement('a');
            link.href = window.URL.createObjectURL(new Blob([response.data]));
            link.download = displayName || fileName;
            link.click();
            window.URL.revokeObjectURL(link.href);
        } catch (err) {
            console.error('Failed to download file:', err);
        }
    };

    return (
        <Card
            size="small"
            hoverable
            style={{ maxWidth: 360, width: '100%', cursor: 'pointer' }}
            bodyStyle={{ padding: '8px 12px', display: 'flex', alignItems: 'center', gap: 8, minWidth: 0, overflow: 'hidden' }}
            onClick={handleDownload}
        >
            {iconForPath(filePath)}
            <Text ellipsis={{ tooltip: fileName }} style={{ flex: 1, minWidth: 0 }}>{displayName}</Text>
            <Button type="text" size="small" icon={<DownloadOutlined />} onClick={handleDownload} aria-label={i18next.t('Download')} />
        </Card>
    );
};

// Helper function to render fields
export const renderField = (fieldName, fieldProps, initialDto, handleInputChange, handleBrowseEntity, userInfo) => {

    const {
        statusOptions,
        bloodTypeOptions,
        genderOptions,
        encounterTypeOptions,
        orderTypeOptions,
        sampleTypeOptions,
        containerTypeOptions,
        qcResultStatusOptions,
        subscriptionPackageOptions,
        patientEventGroupOptions,
        languageOptions,
        isNhsNumberValidOptions,
        dataQualityStatusOptions,
    } = getTranslatedOptions();


    const fieldType = fieldProps.type;
    const isReadOnly = fieldProps.isReadOnly;
    const value = initialDto[convertKeyToCamelCase(fieldName)];

    switch (fieldType) {
        case 'String':
            switch (fieldName) {
                case 'FilePath':
                    return <FilePathCard filePath={value} emptyText={i18next.t('No file uploaded')} userInfo={userInfo} />;
                case 'MatFilePath':
                    return <FilePathCard filePath={value} emptyText={i18next.t('Not yet generated')} userInfo={userInfo} />;
                default:
                    break;
            }
            if (isReadOnly) {
                return <span>{value || '-'}</span>;
            }
            switch (fieldName) {
                case 'Password':
                case 'NifiPassword':
                case 'SupersetPassword':
                case 'SftpPassword':
                case 'DatabaseConnectionString':
                case 'FhirServerPassword':
                    return <Input.Password placeholder={`Enter ${fieldName}`} onBlur={(e) => handleInputChange(fieldName, e.target.value)} />;
                case 'Email':
                    return <Input placeholder={`Enter ${fieldName}`} type="email" onBlur={(e) => handleInputChange(fieldName, e.target.value)} />;
                case 'PatientIdentifier':
                    // Editable; blank on create → backend assigns PAT… (PatientRepository).
                    return (
                        <Input
                            placeholder={i18next.t('Leave blank to assign automatically')}
                            value={value || ''}
                            onChange={(e) => handleInputChange(fieldName, e.target.value)}
                        />
                    );
                case 'NHSNumber':
                    return (
                        <Input
                            placeholder="10 digit NHS number"
                            maxLength={10}
                            value={value || ''}
                            onChange={(e) => handleInputChange(fieldName, e.target.value)}
                            onBlur={(e) => handleInputChange(fieldName, e.target.value.trim() || null)}
                        />
                    );
                case 'CHINumber':
                    return (
                        <Input
                            placeholder="10 digit CHI number"
                            maxLength={10}
                            value={value || ''}
                            onChange={(e) => handleInputChange(fieldName, e.target.value)}
                            onBlur={(e) => handleInputChange(fieldName, e.target.value.trim() || null)}
                        />
                    );
                case 'Postcode':
                    return (
                        <Input
                            placeholder="UK postcode"
                            maxLength={8}
                            value={value || ''}
                            onChange={(e) => handleInputChange(fieldName, e.target.value)}
                            onBlur={(e) => handleInputChange(fieldName, e.target.value.trim().toUpperCase() || null)}
                        />
                    );
                case 'CohortName':
                case 'cohortName':
                    return (
                        <Space.Compact style={{ width: '100%' }}>
                            <Input
                                value={value}
                                placeholder="SESSION-MM-YYYY-ORGID"
                                onChange={(e) => handleInputChange(fieldName, e.target.value)}
                            />
                            <Button
                                type="default"
                                onClick={() => handleInputChange(fieldName, generateSessionName(userInfo))}
                            >
                                {i18next.t('Generate New')}
                            </Button>
                        </Space.Compact>
                    );
                
                case 'IdentityUserId':
                    return (
                        fieldEntityActionConfig[fieldName] && (
                            <SingleEntitySelect
                                singularEntity={fieldEntityActionConfig[fieldName].singularEntity}
                                pluralEntity={fieldEntityActionConfig[fieldName].pluralEntity}
                                constantPrefix={fieldEntityActionConfig[fieldName].constantPrefix}
                                listEntities={fieldEntityActionConfig[fieldName].listEntities}
                                onChange={(value) => handleInputChange(fieldName, value)}
                                value={value}
                                primarySearchProp={fieldEntityActionConfig[fieldName].primarySearchProp}
                                secondarySearchProp={fieldEntityActionConfig[fieldName].secondarySearchProp}
                                enableCreate={false}
                            />
                        )
                    );
                case 'Notes':
                case 'Comments':
                case 'Description':
                case 'ReportData':
                case 'StreetAddress':
                case 'Method':
                case 'Interpretation':
                case 'AdditionalDemographicInformation':
                case 'AdditionalClinicalInformation':
                case 'ConsentScope':
                case 'ConsentPurpose':
                case 'ConsentAdditionalInfo':
                case 'ReportConclusion':
                case 'SampleDescription':
                case 'CollectionMethod':
                case 'StorageConditions':
                case 'HandlingInstructions':
                case 'History':
                case 'ConservationMethod':
                case 'QCNotes':
                case 'HtmlContent':
                case 'RawContent':
                case 'HowToUse':
                case 'WithdrawalReason':
                case 'StudyRole':
                    return <Input.TextArea style={{ minHeight: '300px' }} placeholder={`Enter ${fieldName}`} onBlur={(e) => handleInputChange(fieldName, e.target.value)} />;
                case 'TemplateContent':
                    return (
                        <TemplateContentEditor
                            initialValue={value}
                            onCommit={(nextValue) => handleInputChange(fieldName, nextValue)}
                        />
                    );
                case 'Status':
                case 'ConsentStatus':
                case 'PaymentStatus':
                case 'CurrentStatus':
                case 'InvoiceStatus':
                case 'QCScheduleStatus':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value}
                            defaultValue={statusOptions[0].value}
                        >
                            {statusOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );
                case 'Language':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value}
                        >
                            {languageOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );

                case 'PatientEventGroup':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value}
                        >
                            {patientEventGroupOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );

                case 'QCResultStatus':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value}
                            defaultValue={qcResultStatusOptions[0].value}
                        >
                            {qcResultStatusOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );

                case 'DataQualityStatus':
                    if (isReadOnly) {
                        const ragColor = { R: 'red', A: 'orange', G: 'green', Red: 'red', Amber: 'orange', Green: 'green' }[value] || 'default';
                        const ragLabel = dataQualityStatusOptions?.find(o => o.value === value)?.label ?? value ?? '-';
                        return <Tag color={ragColor}>{ragLabel}</Tag>;
                    }
                    return (
                        <Select
                            placeholder={i18next.t('Select Data Quality (RAG)')}
                            onChange={(val) => handleInputChange(fieldName, val)}
                            value={value}
                        >
                            {dataQualityStatusOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );

                case 'Barcode':
                    return (
                        <RandomBarcode
                            startsWith={"120"}
                            length={12}
                            format={"CODE128"}
                            width={2}
                            height={50}
                            fontSize={12}
                            inputValue={initialDto.barcode}
                            onValueChange={(e) => handleInputChange(fieldName, e.target.value)}
                        />);
                case 'OrderNumber':
                    return (
                        <EntityNumberInput
                            inputValue={initialDto.orderNumber}
                            prefix={"ORD"}
                            onValueChange={(e) => {
                                handleInputChange(fieldName, e.target.value);
                            }}
                        />);
                case 'OrderCode':
                    return (
                        <EntityCodeInput
                            inputValue={initialDto.orderCode}
                            prefix={"ORD"}
                            onValueChange={(e) => {
                                handleInputChange(fieldName, e.target.value);
                            }
                            }
                        />);
                case 'ReferenceCode':
                    return (
                        <EntityCodeInput
                            inputValue={initialDto.referenceCode}
                            prefix={"REF"}
                            onValueChange={(e) => {
                                handleInputChange(fieldName, e.target.value);
                            }
                            }
                        />);
                case 'SampleCode':
                    return (
                        <EntityCodeInput
                            inputValue={initialDto.sampleCode}
                            prefix={"SMP"}
                            onValueChange={(e) => {
                                handleInputChange(fieldName, e.target.value);
                            }
                            }
                        />);

                case 'LotCode':
                    return (
                        <EntityCodeInput
                            inputValue={initialDto.lotCode}
                            prefix={"LOT"}
                            onValueChange={(e) => {
                                handleInputChange(fieldName, e.target.value);
                            }
                            }
                        />);
                case 'InventoryCode':
                    return (
                        <EntityCodeInput
                            inputValue={initialDto.inventoryCode}
                            prefix={"INV"}
                            onValueChange={(e) => {
                                handleInputChange(fieldName, e.target.value);
                            }
                            }
                        />);

                case 'Identifier':
                    return (
                        <EntityCodeInput
                            inputValue={initialDto.identifier}
                            prefix={"ORG"}
                            onValueChange={(e) => {
                                handleInputChange(fieldName, e.target.value);
                            }
                            }
                        />);

                case 'ReferenceNumber':
                    return (
                        <EntityCodeInput
                            inputValue={initialDto.referenceNumber}
                            prefix={"REF"}
                            onValueChange={(e) => {
                                handleInputChange(fieldName, e.target.value);
                            }
                            }
                        />);

                case 'SubscriptionPackage':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value}
                        >
                            {subscriptionPackageOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );

                case 'BloodType':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value}
                        >
                            {bloodTypeOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );

                case 'SampleType':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value}
                        >
                            {sampleTypeOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );

                case 'EncounterType':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value}
                        >
                            {encounterTypeOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );
                case 'ContainerType':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value}
                        >
                            {containerTypeOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );

                case 'OrderType':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value}
                            defaultValue={orderTypeOptions[0].value}
                        >
                            {orderTypeOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );
                case 'Gender':
                    return (
                        <Select
                            placeholder={`Select ${fieldName}`}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            value={value || undefined}
                            allowClear
                        >
                            {genderOptions?.map(option => (
                                <Select.Option key={option.value} value={option.value}>
                                    {option.label}
                                </Select.Option>
                            ))}
                        </Select>
                    );
              

                case 'Documents':
                case 'QCRuleDocuments':
                case 'QCResultDocuments':
                case 'QCReportDocuments':
                case 'ConsentDocument':
                    return (
                        <FileUploaderAndViewer
                            value={initialDto[fieldName.toLowerCase()] || "[]"}
                            onChange={(value) => handleInputChange(fieldName, value)}
                            category={fieldName.toLowerCase()}
                            ownerId={initialDto.id}
                            ownerName={`${initialDto.name || initialDto.forename || ''} ${initialDto.surname || ''}`.trim() || 'unknown'}
                            bucketName={userInfo.businessOrganisationId ? userInfo.businessOrganisationId : null}
                        />
                    );

                case 'Alert':
                case 'CareActivityCategoryName':
                    return (
                        <Tag color={value}>{value}</Tag>
                    );

                default:
                    return <Input placeholder={`Enter ${fieldName}`} value={value} onBlur={(e) => handleInputChange(fieldName, e.target.value)} />;
            }
        case 'List`1':
            return (
                <Space>
                    <Button type="primary" size='small' icon={<FileSearchOutlined />} onClick={() => handleBrowseEntity(fieldEntityActionConfig[fieldName])}>Browse</Button>
                    {value && value.length > 0 ? `${value.length} item(s) selected` : '-'}
                </Space>
            );
        case 'JsonDocument':
            case 'ValidationHistory':
                return (
                    <Tag color='default'>{'This field can not be edited here'}</Tag>
                );
            
            return <Input.TextArea placeholder={`Enter ${fieldName}`} value={JSON.stringify(value, null, 2)} onBlur={(e) => handleInputChange(fieldName, e.target.value)} />;
        case 'Decimal':
            switch (fieldName) {
                case 'ExpectedValue':
                case 'StandardDeviation':
                case 'AcceptableLimitLow':
                case 'AcceptableLimitHigh':
                case 'WarningLimitLow':
                case 'WarningLimitHigh':
                case 'GeographicRadiusMiles':
                    // return distance with suffix miles
                    return <Input suffix='miles' placeholder={`Example 10.8`} value={value} onBlur={(e) => handleInputChange(fieldName, parseFloat(e.target.value))} />;
                case 'CenterLatitude':
                case 'CenterLongitude':
                    return <Input suffix='degrees' placeholder={`Example 51.5074`} value={value} onBlur={(e) => handleInputChange(fieldName, parseFloat(e.target.value))} />;
                    case 'StandardDuration':
                    return (
                        <Input placeholder={`Enter ${fieldName}`} value={value} onBlur={(e) => handleInputChange(fieldName, e.target.value)} />
                    );

                case 'DefaultRate':
                case 'PercentageIncrease':
                case 'Tax':
                case 'Rate':
                    return (
                        <Input suffix='%' placeholder={`Enter ${fieldName}`} value={value} onBlur={(e) => handleInputChange(fieldName, e.target.value)} />
                    );

                default:
                    const currencySymbol = userInfo?.currencySymbol || '£';
                    const currencyCode = userInfo?.currencyCode || 'GBP';
                    return (
                        <Input prefix={currencySymbol} suffix={currencyCode} placeholder={`Enter ${fieldName}`} value={value} onBlur={(e) => handleInputChange(fieldName, e.target.value)} />
                    );
            }
        case 'Int32':
            return (
                <Input type="number" value={value} placeholder={`Enter ${fieldName}`} onBlur={(e) => handleInputChange(fieldName, parseInt(e.target.value))} />
            );
        case 'Guid':
        case 'Nullable`1':
            if (isReadOnly) {
                return <Tag color='default'>{value || '-'}</Tag>;
            }
            switch (fieldName) {
                case 'CareActivityInventoryLineId':
                case 'CareActivityOrderId':
                case 'CareActivityOrderBulkRequestId':
                    return <Tag color='default'>This field can't be edited here</Tag>;
                case 'CareActivityOutcomeReportId':
                case 'InvoiceReportId':
                    return (
                        <Space>
                            <Button type="primary" size='small' icon={<FileSearchOutlined />} onClick={() => handleBrowseEntity(fieldEntityActionConfig[fieldName])}>View</Button>
                        </Space>
                    );
                case 'IdentityUserId':
                    return (
                        fieldEntityActionConfig[fieldName] && (
                            <SingleEntitySelect
                                singularEntity={fieldEntityActionConfig[fieldName].singularEntity}
                                pluralEntity={fieldEntityActionConfig[fieldName].pluralEntity}
                                constantPrefix={fieldEntityActionConfig[fieldName].constantPrefix}
                                listEntities={fieldEntityActionConfig[fieldName].listEntities}
                                onChange={(value) => handleInputChange(fieldName, value)}
                                value={value}
                                primarySearchProp={fieldEntityActionConfig[fieldName].primarySearchProp}
                                secondarySearchProp={fieldEntityActionConfig[fieldName].secondarySearchProp}
                                enableCreate={false}
                            />
                        )
                    );
                case 'PatientId':
                    return (
                        <QuickPatientSelect
                            value={value}
                            onChange={(patientId) => handleInputChange(fieldName, patientId)}
                        />
                    );
                default:
                    return (
                        fieldEntityActionConfig[fieldName] && (
                            <SingleEntitySelect
                                singularEntity={fieldEntityActionConfig[fieldName].singularEntity}
                                pluralEntity={fieldEntityActionConfig[fieldName].pluralEntity}
                                constantPrefix={fieldEntityActionConfig[fieldName].constantPrefix}
                                listEntities={fieldEntityActionConfig[fieldName].listEntities}
                                onChange={(value) => handleInputChange(fieldName, value)}
                                value={value}
                                primarySearchProp={fieldEntityActionConfig[fieldName].primarySearchProp}
                                secondarySearchProp={fieldEntityActionConfig[fieldName].secondarySearchProp}
                            />
                        )
                    );
            }
        case 'Boolean':
            switch (fieldName) {
                case 'ValidationStatus':
                    return (
                        <Tag
                            color={value ? 'success' : 'error'}
                            icon={value ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        >
                            {value ? 'Valid' : 'Invalid'}
                        </Tag>
                    );
                case 'IsNhsNumberValid':
                    if (isReadOnly) {
                        const label = value === true ? (isNhsNumberValidOptions?.find(o => o.value === true)?.label ?? 'Yes') : (isNhsNumberValidOptions?.find(o => o.value === false)?.label ?? 'No');
                        return (
                            <Tag color={value ? 'success' : 'default'}>
                                {label}
                            </Tag>
                        );
                    }
                    return (
                        <Switch
                            checked={value === true}
                            onChange={(checked) => handleInputChange(fieldName, checked)}
                        />
                    );
                default:
                    return (
                        <Switch
                            defaultChecked={value}
                            onChange={(checked) => handleInputChange(fieldName, checked)}
                        />
                    );
            }
        case 'DateTime':
            switch (fieldName) {
                case 'DateOfBirth':
                case 'DateOfDeath':
                    return (
                        <DatetimeInput
                            fieldName={fieldName}
                            value={value}
                            handleInputChange={handleInputChange}
                            dateOnly
                        />
                    );
                default:
                    return (
                        <DatetimeInput
                            fieldName={fieldName}
                            value={value}
                            handleInputChange={handleInputChange}
                        />
                    );
            }
        default:
            return <span>{value || '-'}</span>;
    }
};

